import React from "react";
import {
  AnimatedItem,
  Body,
  css,
  Display,
  Heading,
  styled,
  useIsSmallScreenSize,
} from "@kivra/react-components";

const TYPOGRAPHY_MAX_WIDTH_PX = 480;
const CARD_MAX_WIDTH = "100%";

const CardWrapper = styled("div")({
  $small: {
    width: "100%",
  },
  width: CARD_MAX_WIDTH,
  minHeight: 400,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
});

const Card: React.FC<React.HTMLAttributes<HTMLDivElement>> & {
  Title: typeof CardTitle;
  Text: typeof CardText;
  ButtonGroup: typeof CardButtonGroup;
} = (props) => (
  <AnimatedItem animation="fade-in">
    <CardWrapper>{props.children}</CardWrapper>
  </AnimatedItem>
);

function CardTitle(props: { children: string }) {
  const isSmallScreenSize = useIsSmallScreenSize();

  if (isSmallScreenSize) {
    return (
      <Heading
        size="large"
        className={css({
          maxWidth: TYPOGRAPHY_MAX_WIDTH_PX,
          wordBreak: "break-word",
        })}
      >
        {props.children}
      </Heading>
    );
  }

  return (
    <Display
      size="medium"
      className={css({ maxWidth: TYPOGRAPHY_MAX_WIDTH_PX })}
    >
      {props.children}
    </Display>
  );
}

function CardText(props: { children: React.ReactNode }) {
  return (
    <Body
      size="medium"
      className={css({ maxWidth: TYPOGRAPHY_MAX_WIDTH_PX })}
    >
      {props.children}
    </Body>
  );
}

const CardButtonGroup:
  & React.FC<
    React.HTMLAttributes<HTMLDivElement> & {
      overrideCss?: Parameters<typeof css>[0];
    }
  >
  & {
    Spacer: typeof CardButtonGroupSpacer;
  } = ({ overrideCss, ...props }) => {
    const className = css({
      width: "100%",
      paddingTop: "$spacing-40",
      marginTop: "auto",
      display: "flex",
      justifyContent: "start",
      flexWrap: "wrap",
      $small: {
        flexDirection: "column",
      },
      ...overrideCss,
    });

    return <div className={className} {...props} />;
  };

const CardButtonGroupSpacer = styled.div({
  margin: [0, 0, 0, "$spacing-16"],
  $small: {
    margin: ["$spacing-16", 0, 0, 0],
  },
});

CardButtonGroup.Spacer = CardButtonGroupSpacer;

Card.Title = CardTitle;
Card.Text = CardText;
Card.ButtonGroup = CardButtonGroup;

export { Card };
